import React, { useState, useEffect } from 'react';
import { Lock, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { supabase, isSupabaseConfigured } from './supabase';

interface ResetPasswordPageProps {
  /** Hova vigyen a sikeres jelszóváltás után. */
  loginPath?: string;
  /** Az alkalmazás neve a fejlécben. */
  appName?: string;
}

const MIN_PASSWORD_LENGTH = 8;

/**
 * Új jelszó beállítása a jelszó-visszaállító e-mail linkjéről érkezve.
 *
 * ── Miért KÜLÖN, nyilvános oldal ──────────────────────────────────────────
 * A Supabase a helyreállító munkamenetet az URL FRAGMENTJÉBEN küldi vissza
 * (`#access_token=…&type=recovery`), amit a supabase-js aszinkron dolgoz fel.
 * A korábbi cél a `/profile` volt, ami bejelentkezést követel: amíg a
 * munkamenet felállt volna, az őr átirányított a bejelentkezésre — méghozzá
 * `pathname + search` alapján, ami a FRAGMENTET eldobja. A helyreállító
 * token ezzel véglegesen elveszett, és a felhasználó egy olyan bejelentkező
 * képernyőn kötött ki, ahol épp a jelszava hiányzott.
 *
 * Ez az oldal ezért nyilvános, és nem irányít el semmilyen esetben: megvárja,
 * amíg a munkamenet felépül, és csak az új jelszó megadását kéri.
 */
export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  loginPath = '/login',
  appName = 'ReHome',
}) => {
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid' | 'done'>('checking');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setStatus('invalid');
      setLinkError('A Supabase nincs konfigurálva.');
      return;
    }

    // A lejárt vagy már felhasznált link hibát tesz a fragmentbe — ezt
    // konkrét üzenetté fordítjuk, mert a "nem működik" itt semmit nem segít.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const hashError = hash.get('error_description') || hash.get('error');
    if (hashError) {
      setStatus('invalid');
      setLinkError(
        /expired|invalid/i.test(hashError)
          ? 'Ez a link lejárt vagy már felhasználták. Kérj újat a bejelentkezésnél.'
          : decodeURIComponent(hashError.replace(/\+/g, ' '))
      );
      return;
    }

    let cancelled = false;

    // A supabase-js a fragmentből magától felépíti a munkamenetet; itt csak
    // megvárjuk. Az eseményfigyelő azért kell, mert a feldolgozás a
    // getSession() hívása UTÁN is befejeződhet.
    const settle = (hasSession: boolean) => {
      if (cancelled) return;
      setStatus(hasSession ? 'ready' : 'invalid');
      if (!hasSession) {
        setLinkError('Nem sikerült azonosítani a visszaállítási kérelmet. Kérj új linket a bejelentkezésnél.');
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) settle(true);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) settle(true);
      // Ha még nincs: nem nyilvánítjuk azonnal érvénytelennek. A fragment
      // feldolgozása befejeződhet ezután is, és akkor az eseményfigyelő szól.
    });

    // Végső határidő: ha eddig sem lett munkamenet, a link nem használható.
    const timeout = setTimeout(() => {
      supabase.auth.getSession().then(({ data: { session } }) => settle(Boolean(session)));
    }, 2500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_PASSWORD_LENGTH && password === confirm && !saving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);

    if (updateError) {
      setError(updateError.message || 'A jelszó módosítása nem sikerült.');
      return;
    }

    // A helyreállító munkamenetet lezárjuk: a felhasználó az ÚJ jelszavával
    // lépjen be, így meg is győződik róla, hogy tényleg működik.
    await supabase.auth.signOut();
    setStatus('done');
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 bg-teal-50 rounded-lg"><Lock className="w-5 h-5 text-teal-600" /></div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Új jelszó beállítása</h1>
            <p className="text-xs text-gray-400">{appName}</p>
          </div>
        </div>

        {status === 'checking' && (
          <div className="py-10 flex flex-col items-center gap-3 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">Visszaállítási kérelem ellenőrzése…</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{linkError}</p>
            </div>
            <a href={loginPath} className="block text-center bg-teal-600 text-white py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-colors">
              Vissza a bejelentkezéshez
            </a>
          </div>
        )}

        {status === 'done' && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">
                A jelszavad megváltozott. Jelentkezz be az új jelszavaddal.
              </p>
            </div>
            <a href={loginPath} className="block text-center bg-teal-600 text-white py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-colors">
              Bejelentkezés
            </a>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">Új jelszó</label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className={inputCls}
                  placeholder={`Legalább ${MIN_PASSWORD_LENGTH} karakter`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {tooShort && (
                <p className="text-xs text-amber-600 mt-1">Még {MIN_PASSWORD_LENGTH - password.length} karakter szükséges.</p>
              )}
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">Új jelszó mégegyszer</label>
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                className={inputCls}
              />
              {mismatch && <p className="text-xs text-red-600 mt-1">A két jelszó nem egyezik.</p>}
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-teal-600 text-white py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Jelszó mentése
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
